import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import countries from 'i18n-iso-countries';
import {
  Box,
  Button,
  Paper,
  Typography,
  Stepper,
  Step,
  StepContent,
  StepLabel,
  Divider,
} from '@mui/material';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Form, FormRenderProps } from 'react-final-form';
import queryString from 'query-string';
import {
  loggedOut,
  loggedIn,
} from '../redux/actions';
import ReactFinalFormWrapper from '../components/register/ReactFinalFormWrapper';
import registerFormConfig from '../components/register/registerFormConfig';
import { RootState, UserRegistrationData } from '../redux/types';
import ApiClient from '../loopback/apiClient';
import RegisterStepButtons from '../components/register/RegisterStepButtons';
import { ReactFinalFormCheckBox } from '../components/register/ReactFinalFormFields';
import Prompt from '../components/layout/Prompt';
import withRouter, { RouteType } from '../hoc/withRouter';

type State = {
  currentStep: number;
  stepErrors: { [key: string]: boolean };
  isSkipped: boolean;
  isFromVoucher: boolean;
  showSkipPrompt: boolean;
}

type Props = PropsFromRedux & RouteType;
type formTypes = {
  label: React.ReactNode,
  data: any;
};

class RegisterView extends Component<Props, State> {
  // if skip institute registration data
  static resetInstituteData = (values: any): void => {
    // this is mutatable
    if (values.institute) {
      // eslint-disable-next-line no-param-reassign
      delete values.institute;
    }
  };

  static parseRegistrationValueOverview = (values: Record<string, any>): formTypes[] => {
    const { institute } = values;
    const user = { ...values };
    delete user.institute;
    delete user.userPassword;
    delete user.userPasswordConfirm;
    delete user.acceptAGB;
    delete user.newsLetterOptIn;
    const sectionsToPrint: formTypes[] = [];
    sectionsToPrint.push(
      {
        label: <FormattedMessage id="registration.personalInformationHeading" />, data: user,
      },
    );

    if (institute) {
      const newInstitute = {
        ...institute,
      };
      if (institute.custom) {
        Object.assign(newInstitute, { 'info.custom': 'Custom Information', ...institute.custom });
        delete newInstitute.custom;
      }
      if (institute.invoice) {
        Object.assign(newInstitute, { 'info.invoice': 'Custom Information', ...institute.invoice });
        delete newInstitute.invoice;
      }
      if (institute.certificate) {
        Object.assign(newInstitute, { 'info.certificate': 'Custom Information', ...institute.certificate });
        delete newInstitute.certificate;
      }
      delete newInstitute.invoicedifferent;
      delete newInstitute.certificatedifferent;
      sectionsToPrint.push({
        label: <FormattedMessage id="registration.instituteInformationHeading" />, data: newInstitute,
      });
    }

    return sectionsToPrint;
  };

  data = {};

  constructor(props: Props) {
    super(props);
    this.state = {
      currentStep: 0,
      stepErrors: {},
      isSkipped: false,
      isFromVoucher: false,
      showSkipPrompt: false,
    };
  }

  componentDidMount() {
    const {
      router,
      loggedOut: loggedOutAction,
      token,
    } = this.props;
    const qParams = queryString.parse(router.location.search);
    if (qParams && qParams.origin === 'voucher') {
      this.setState({ isFromVoucher: true });
    }

    if (token && token.id) {
      ApiClient.logout(true).then(loggedOutAction);
    }
  }

  onBack = () => this.setState((state) => ({
    currentStep: state.isSkipped ? 0 : state.currentStep - 1,
  }), () => this.setState({ isSkipped: false }));

  onNext = () => this.setState((state) => ({ currentStep: state.currentStep + 1 }));

  onSetCurrentStep = (stepIdx: number) => this.setState({ currentStep: stepIdx });

  noErrorsOnRegistration = () => {
    const { stepErrors } = this.state;
    return Object.keys(stepErrors).filter((key) => (
      stepErrors[key]
    )).length === 0;
  };

  prepareSubmitData = (data: any) => {
    const { router, locale } = this.props;
    // const { newsletterOptIn } = this.state;
    const queryParams = queryString.parse(router.location.search);
    const userData: UserRegistrationData = {
      gender: data.userGender,
      title: data.userTitle,
      firstname: data.userFirstName.trim(),
      surname: data.userLastName.trim(),
      email: data.userEmail.trim(),
      password: data.userPassword.trim(),
      commLanguage: locale,
      newsletterOptIn: data.newsLetterOptIn,
      userCountry: data.userCountry,
      forceEmailVerified: (queryParams.origin && queryParams.origin === 'voucher' ? true : undefined),
    };
    if (data.institute) {
      const { institute } = data;
      const { custom, invoice, certificate } = institute;
      const mainInstituteData = { ...institute, ...custom };
      userData.registrationdata = {
        instituteData: mainInstituteData,
        invoiceData: invoice,
        certificationData: certificate,
      };
    }
    return userData;
  };

  handleRedirect = () => {
    const { router } = this.props;
    const { isFromVoucher } = this.state;
    if (isFromVoucher) {
      router.navigate('/voucherenroll');
    } else {
      router.navigate('/login');
    }
  };

  onSubmit = async (values: unknown) => {
    const { loggedIn: loggedInAction } = this.props;
    const { isFromVoucher } = this.state;

    const regData = this.prepareSubmitData(values);
    await ApiClient.registerUser(regData);
    if (isFromVoucher) {
      const newToken = await ApiClient.login(regData.email, regData.password);
      loggedInAction(regData.email, newToken);
    }
    this.handleRedirect();
  };

  render() {
    const { locale } = this.props;
    const { currentStep, isFromVoucher, showSkipPrompt } = this.state;
    const formConfigurations = registerFormConfig(locale);
    const configurationLength = formConfigurations.length;
    return (
      <Paper sx={{ p: 15 }}>
        <Typography variant="h5"><FormattedMessage id="registration.headline" /></Typography>
        <span className="f6 moon-gray"><FormattedMessage id="registration.requiredFieldsText" /></span>
        <Form
          onSubmit={this.onSubmit}
          initialValues={this.data}
          render={({ form, values }: FormRenderProps) => {
            const containsError = Object.keys(form.getState().errors || {}).length > 0;
            return (
              <>
                <Stepper
                  activeStep={currentStep}
                  orientation="vertical"
                >
                  {
                    formConfigurations.map((formConfiguration, idx) => (
                      <Step key={`step-${formConfiguration.formRef}`}>
                        <StepLabel
                          icon={idx + 1}
                        >
                          {formConfiguration.label}
                        </StepLabel>
                        <StepContent>
                          <form className="flex flex-wrap">
                            <ReactFinalFormWrapper registrationForm={formConfiguration} />
                            <RegisterStepButtons
                              firstStep={currentStep === 0}
                              onNext={!containsError ? this.onNext : undefined}
                              onBack={this.onBack}
                            />
                            {currentStep === 0 && !isFromVoucher && (
                              <Button
                                onClick={() => {
                                  RegisterView.resetInstituteData(values);
                                  this.setState({ isSkipped: true });
                                  this.onSetCurrentStep(configurationLength);
                                  this.setState({ showSkipPrompt: true });
                                }}
                                disabled={containsError}
                              >
                                <FormattedMessage id="registration.skipandsubmit" />
                              </Button>
                            )}
                          </form>
                        </StepContent>
                      </Step>
                    ))
                  }
                  <Step key="step-final">
                    <StepLabel icon={configurationLength + 1}>
                      <FormattedMessage id="registration.overview" />
                    </StepLabel>
                    <StepContent>
                      {RegisterView.parseRegistrationValueOverview(values).map((data) => (
                        <div key={`overview-${data.data.key}`}>
                          <h3 className="color-quip mt2 mb2 f4">
                            {data?.label}
                            <Divider />
                          </h3>
                          {
                            Object.keys(data.data).map((key) => {
                              if (key === 'info.custom'
                                || key === 'info.invoice'
                                || key === 'info.certificate') {
                                return (
                                  <>
                                    <Divider />
                                    <h3 className="color-quip mt2 mb2 f4"><FormattedMessage id={`registration.${key}`} /></h3>
                                  </>
                                );
                              }
                              return (
                                <Box key={key} display="flex">
                                  <Typography color="GrayText" flexBasis="150px">
                                    <FormattedMessage id={`registration.${key}`} />
                                  </Typography>
                                  <Typography>
                                    {
                                      (() => {
                                        if (data.data[key] === 'male') {
                                          return <FormattedMessage id="salutation.mr" />;
                                        }
                                        if (data.data[key] === 'female') {
                                          return <FormattedMessage id="salutation.ms" />;
                                        }
                                        if (key === 'userCountry') {
                                          return countries.getName(data.data[key], locale);
                                        }
                                        return data.data[key];
                                      })()
                                    }
                                  </Typography>
                                </Box>
                              );
                            })
                          }
                        </div>
                      ))}
                      <form className="flex flex-wrap mt4">
                        <div className="basis-100">
                          <ReactFinalFormCheckBox
                            label={<FormattedMessage id="registration.newsletterOptIn" />}
                            keyValue="newsLetterOptIn"
                            defaultValue={false}
                          />
                        </div>
                        <div className="basis-100">
                          <ReactFinalFormCheckBox
                            label={<FormattedMessage id="registration.userAGBAcceptLabel" />}
                            keyValue="acceptAGB"
                            columnClassName="w-100"
                            defaultValue={false}
                            validate={(value) => (!value ? <FormattedMessage id="registration.agbAccept" /> : '')}
                          />
                        </div>
                      </form>
                      <RegisterStepButtons
                        lastStep
                        onNext={this.onNext}
                        onBack={this.onBack}
                        onSubmit={!containsError ? () => this.onSubmit(values) : undefined}
                      />
                    </StepContent>
                  </Step>
                </Stepper>
              </>
            );
          }}
        />
        <Prompt
          display={showSkipPrompt}
          action={() => { this.setState({ showSkipPrompt: false }); }}
          title={<FormattedMessage id="note" />}
          alert
        >
          <FormattedMessage id="registration.skipWarning" />
        </Prompt>
      </Paper>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  locale: state.intl.locale,
  token: state.users.token,
});

const dispatchProps = {
  loggedOut,
  loggedIn,
};

type PropsFromRedux = ConnectedProps<typeof connector>;
const connector = connect(mapStateToProps, dispatchProps);
export default injectIntl(connector(withRouter(RegisterView)));
